// src/components/customer/CartSheet.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../lib/AppContext';
import { t } from '../../lib/i18n';
import { formatEurCheckout } from '../../lib/currency';

const FREE_SHIPPING_THRESHOLD = 60;

export default function CartSheet() {
  const {
    lang, cartItems, cartSubtotalEur, cartShippingEur, cartTotalEur,
    cartCount, removeFromCart, updateQty, dispatch, fmt, user,
  } = useApp();
  const navigate = useNavigate();

  const handleCheckout = () => {
    dispatch({ type: 'CART_CLOSE' });
    navigate(user ? '/checkout' : '/login');
  };

  const remaining   = Math.max(0, FREE_SHIPPING_THRESHOLD - cartSubtotalEur);
  const progressPct = Math.min((cartSubtotalEur / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const freeShipping = cartSubtotalEur >= FREE_SHIPPING_THRESHOLD;

  return (
    <>
      <div className="cart-overlay" onClick={() => dispatch({ type: 'CART_CLOSE' })} />
      <div className="cart-drawer">

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,.07)', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.35rem', letterSpacing: '.05em' }}>
              {t('your_cart', lang)}
            </div>
            <div style={{ fontSize: '.75rem', color: '#555', marginTop: 1 }}>
              {cartCount} item{cartCount !== 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={() => dispatch({ type: 'CART_CLOSE' })}
            style={{ background: 'rgba(255,255,255,.06)', border: 'none', color: '#888', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ✕
          </button>
        </div>

        {/* ── Free shipping progress ── */}
        {cartItems.length > 0 && (
          <div style={{
            padding: '10px 20px',
            background: freeShipping ? 'rgba(76,202,116,.06)' : 'rgba(255,255,255,.02)',
            borderBottom: '1px solid rgba(255,255,255,.05)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: '.72rem', color: freeShipping ? '#4cca74' : '#777' }}>
                {freeShipping
                  ? '✓ Free shipping unlocked!'
                  : `Add €${remaining.toFixed(2)} more for free shipping`}
              </span>
              <span style={{ fontSize: '.68rem', color: '#444' }}>€{FREE_SHIPPING_THRESHOLD}</span>
            </div>
            <div style={{ height: 3, background: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                background: freeShipping ? '#4cca74' : 'linear-gradient(90deg, #4cca74 0%, #3bb85e 100%)',
                borderRadius: 2,
                width: `${progressPct}%`,
                transition: 'width .4s ease',
              }} />
            </div>
          </div>
        )}

        {/* ── Items ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {cartItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 0' }}>
              <div style={{ fontSize: '3rem', opacity: .12, marginBottom: '1rem' }}>🌿</div>
              <p style={{ color: '#444', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.1em', textTransform: 'uppercase', fontSize: '.85rem' }}>
                {t('empty_cart', lang)}
              </p>
              <button
                onClick={() => dispatch({ type: 'CART_CLOSE' })}
                style={{ marginTop: 16, background: 'none', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, padding: '8px 20px', color: '#666', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.8rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            cartItems.map(item => {
              const name = item.name?.[lang] || item.name?.en || 'Product';
              const atMax = item.qty >= (item.stock || 99);
              return (
                <div key={item.id} style={{
                  display: 'flex', gap: 12, alignItems: 'center',
                  padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,.05)',
                }}>
                  {/* Thumbnail */}
                  <div style={{
                    width: 56, height: 56, borderRadius: 10, background: '#1a1a1a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.4rem', flexShrink: 0, overflow: 'hidden',
                  }}>
                    {item.images?.[0]
                      ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : '🌿'
                    }
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: '.88rem', fontWeight: 700, color: '#f0f0f0',
                      marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {name}
                    </div>
                    <div style={{ fontSize: '.8rem', color: '#4cca74', fontWeight: 700 }}>
                      {fmt(item.price_eur)}
                    </div>
                    {atMax && (
                      <div style={{ fontSize: '.65rem', color: '#f0a500', marginTop: 2 }}>
                        Max stock reached
                      </div>
                    )}
                  </div>

                  {/* Quantity controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => item.qty <= 1 ? removeFromCart(item.id) : updateQty(item.id, item.qty - 1)}
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: item.qty <= 1 ? 'rgba(224,80,80,.12)' : '#222',
                        border: item.qty <= 1 ? '1px solid rgba(224,80,80,.2)' : '1px solid #333',
                        color: item.qty <= 1 ? '#e05050' : '#f4f4f4',
                        fontSize: '.85rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {item.qty <= 1 ? '✕' : '−'}
                    </button>
                    <span style={{ fontSize: '.9rem', fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                      {item.qty}
                    </span>
                    <button
                      onClick={() => updateQty(item.id, item.qty + 1)}
                      disabled={atMax}
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: atMax ? '#1a1a1a' : '#4cca74',
                        border: 'none', color: '#000', fontSize: '.85rem', cursor: atMax ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: atMax ? .3 : 1, transition: 'opacity .2s',
                      }}
                    >
                      +
                    </button>
                  </div>

                  {/* Line total */}
                  <div style={{ fontSize: '.85rem', fontWeight: 700, color: '#f4f4f4', minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
                    {formatEurCheckout(item.price_eur * item.qty)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Footer ── */}
        {cartItems.length > 0 && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>

            {/* Pricing summary */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '.82rem' }}>
                <span style={{ color: '#666' }}>{t('subtotal', lang)}</span>
                <span>{fmt(cartSubtotalEur)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem' }}>
                <span style={{ color: '#666' }}>{t('shipping', lang)}</span>
                <span style={{ color: freeShipping ? '#4cca74' : '#f4f4f4' }}>
                  {freeShipping ? t('free_shipping', lang) : `+${fmt(cartShippingEur)}`}
                </span>
              </div>
            </div>

            {/* Total — always EUR */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              padding: '12px 0', borderTop: '1px solid rgba(255,255,255,.07)', marginBottom: 12,
            }}>
              <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.88rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                {t('total', lang)}
              </span>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.6rem', color: '#4cca74', letterSpacing: '.04em' }}>
                {formatEurCheckout(cartTotalEur)}
              </span>
            </div>

            {/* Checkout CTA */}
            <button className="btn-primary" style={{ width: '100%', fontSize: '.95rem', padding: '14px' }} onClick={handleCheckout}>
              🔒 {t('checkout', lang)} — {formatEurCheckout(cartTotalEur)}
            </button>

            {/* Trust micro-line */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
              {['🔒 Secure', '📦 Discreet', '↩️ 14-day returns'].map(item => (
                <span key={item} style={{ fontSize: '.65rem', color: '#444', fontFamily: "'Barlow Condensed', sans-serif" }}>{item}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}